import { Host, h, writeTask } from "@stencil/core";
import { mouseEventModifierKey } from "../../../common/helpers";
import { isPseudoElementImg, removeDragImage, tokenMap, updateDirectionInImageCustomVar } from "../../../../common/utils";
import { INITIAL_LEVEL, getTreeItemExpandedPart, getTreeItemLevelPart } from "../../utils";
import { TREE_VIEW_ITEM_CHECKBOX_EXPORT_PARTS, TREE_VIEW_ITEM_EXPORT_PARTS, TREE_VIEW_ITEM_PARTS_DICTIONARY, TREE_VIEW_PARTS_DICTIONARY } from "../../../../common/reserved-names";
import { getControlRegisterProperty } from "../../../../common/registry-properties";
let GET_IMAGE_PATH_CALLBACK_REGISTRY;
const DISTANCE_TO_CHECKBOX_CUSTOM_VAR = "--ch-tree-view-item-distance-to-checkbox";
// Selectors
const TREE_ITEM_TAG_NAME = "ch-tree-view-item";
const DIRECT_TREE_ITEM_CHILDREN = `:scope>${TREE_ITEM_TAG_NAME}`;
const FIRST_ENABLED_SUB_ITEM = `${TREE_ITEM_TAG_NAME}:not([disabled])`;
const LAST_SUB_ITEM = `:scope>${TREE_ITEM_TAG_NAME}:last-child`;
// Custom classes
const DENY_DROP_CLASS = `item-deny-drop`;
// Custom parts
const START_IMAGE_PARTS = `${TREE_VIEW_ITEM_PARTS_DICTIONARY.IMAGE} ${TREE_VIEW_ITEM_PARTS_DICTIONARY.START_IMAGE}`;
const END_IMAGE_PARTS = `${TREE_VIEW_ITEM_PARTS_DICTIONARY.IMAGE} ${TREE_VIEW_ITEM_PARTS_DICTIONARY.END_IMAGE}`;
// Keys
const EXPANDABLE_ID = "expandable";
const ENTER_KEY = "Enter";
const ESCAPE_KEY = "Escape";
/**
 * @part item__action - A sub element of the header (item__header part) that contains the main information related to the item (startImage, caption/edit-caption, endImage and downloading).
 *
 * @part item__checkbox - The host element of the checkbox.
 * @part item__checkbox-container - The container that serves as a wrapper for the `input` and the `option` parts of the checkbox.
 * @part item__checkbox-input - The input element that implements the interactions for the checkbox.
 * @part item__checkbox-option - The actual "input" that is rendered above the `item__checkbox-input` part of the checkbox. This part has `position: absolute` and `pointer-events: none`.
 *
 * @part item__downloading - The spinner element that is rendered when the control is lazy loading its content. This element is rendered at the end of the `item__action` part.
 *
 * @part item__edit-caption - The input element that is rendered when the control is editing its caption. When rendered this element replaces the caption of the `item__action` part.
 *
 * @part item__expandable-button - The actionable expandable button element that is rendered when the control has subitems and the expandable button is interactive (`leaf !== true` and `expandableButton === "action"`). When rendered this element is placed at the start of the `item__action` part.
 *
 * @part item__group - The container element for the subitems of the control that is rendered when the content has been lazy loaded (`leaf !== true` and `lazyLoad !== true`).
 *
 * @part item__header - The container for all elements -excepts the subitems (`item__group` part)- of the control. It contains the `item__expandable-button`, `item_checkbox` and `item__action` parts.
 *
 * @part item__img - The img element that is rendered when the control has images (`startImgSrc` is defined and/or `endImgSrc` is defined).
 *
 * @part item__line - The element that is rendered to display the relationship between the control and its parent. Rendered if `level !== 0` and `showLines !== "none"`.
 *
 * @part disabled - Present in the `item__header`, `item__expandable-button`, `item__checkbox-input`, `item__checkbox-option` and `item__checkbox-container` parts when the control is disabled (`disabled` === `true`).
 *
 * @part expanded - Present in the `item__action`, `item__expandable-button` and `item__group` parts when the control is expanded (`expanded` === `true`).
 * @part collapsed - Present in the `item__action`, `item__expandable-button` and `item__group` parts when the control is collapsed (`expanded` !== `true`).
 *
 * @part expand-button - Present in the `item__header` part when the control has an expandable button (`level !== 0`, `leaf !== true` and `expandableButton !== "no"`).
 *
 * @part even-level - Present in the `item__group` and `item__header` parts when the control is in an even level (`level % 2 === 0`).
 * @part odd-level - Present in the `item__group` and `item__header` parts when the control is in an odd level (`level % 2 !== 0`).
 *
 * @part last-line - Present in the `item__line` part if the control is the last control of its parent item in `showLines = "last"` mode (`showLines === "last"`, `level !== 0` and `lastItem === true`).
 *
 * @part lazy-loaded - Present in the `item__group` part when the content of the control has been loaded (`leaf !== true`, `lazyLoad !== true` and `downloading !== true`).
 *
 * @part start-img - Present in the `item__img` part when the control has an start image element (`startImgSrc` is defined and `startImgType` === "img").
 * @part end-img - Present in the `item__img` part when the control has an end image element (`endImgSrc` is defined and `endImgType` === "img").
 *
 * @part editing - Present in the `item__header` and `item__action` parts when the control is in edit mode (`editable === true` and `editing === true`).
 * @part not-editing - Present in the `item__header` and `item__action` parts when the control isn't in edit mode (`editable !== true` or `editing !== true`).
 *
 * @part selected - Present in the `item__header` part when the control is selected (`selected` === `true`).
 * @part not-selected - Present in the `item__header` part when the control isn't selected (`selected` !== `true`).
 *
 * @part checked - Present in the `item__checkbox-input`, `item__checkbox-option` and `item__checkbox-container` parts when the control is checked and not indeterminate (`checked` === `true` and `indeterminate !== true`).
 * @part indeterminate - Present in the `item__checkbox-input`, `item__checkbox-option` and `item__checkbox-container` parts when the control is indeterminate (`indeterminate` === `true`).
 * @part unchecked - Present in the `item__checkbox-input`, `item__checkbox-option` and `item__checkbox-container` parts when the control is unchecked and not indeterminate (`checked` !== `true` and `indeterminate !== true`).
 *
 * @part drag-enter - Present in the `item__header` part when the control has `dragState === "enter"`.
 */
export class ChTreeViewItem {
    constructor() {
        this.caption = undefined;
        this.checkbox = false;
        this.checked = false;
        this.customRender = false;
        this.disabled = false;
        this.dragDisabled = false;
        this.dropDisabled = false;
        this.dragState = "none";
        this.downloading = false;
        this.editable = undefined;
        this.editing = false;
        this.endImgSrc = undefined;
        this.endImgType = "background";
        this.expandableButton = "decorative";
        this.expandOnClick = true;
        this.expanded = false;
        this.getImagePathCallback = undefined;
        this.lastItem = false;
        this.lazyLoad = false;
        this.leaf = false;
        this.level = INITIAL_LEVEL;
        this.indeterminate = false;
        this.metadata = undefined;
        this.model = undefined;
        this.parts = undefined;
        this.selected = false;
        this.showDownloadingSpinner = true;
        this.showLines = "none";
        this.startImgSrc = undefined;
        this.startImgType = "background";
        this.toggleCheckboxes = false;
    }
    #watcher;
    /**
     * Useful to ignore the checkbox change when it was committed from a children.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #ignoreCheckboxChange = false;
    #startImage;
    #startImageExpanded;
    #endImage;
    #endImageExpanded;
    // Refs
    #headerRef;
    #inputRef;
    el;
    updateChildrenCheckedValue(newValue) {
        if (!this.toggleCheckboxes || this.leaf || this.#ignoreCheckboxChange) {
            this.#ignoreCheckboxChange = false;
            return;
        }
        const treeItems = this.#getDirectTreeItems();
        treeItems.forEach(treeItem => {
            if (treeItem.checked !== newValue || treeItem.indeterminate !== false) {
                treeItem.updateChecked(newValue, false);
            }
        });
    }
    editingChanged(isEditing) {
        if (!isEditing) {
            return;
        }
        document.addEventListener("click", this.#removeEditModeOnClick, {
            capture: true
        });
        // Wait until the input is rendered to focus it
        writeTask(() => {
            requestAnimationFrame(() => {
                if (this.#inputRef) {
                    this.#inputRef.focus();
                }
            });
        });
    }
    endImgSrcChanged(newImage) {
        // Necessary to avoid race condition where the image is updated, the Watch
        // is dispatched and then the model is updated
        this.model.endImgSrc = newImage;
        this.#computeImage("end");
    }
    expandedChanged(isExpanded) {
        // Wait until all properties are updated before lazy loading. Otherwise, the
        // lazyLoad property could be updated just after the executing of the function
        setTimeout(() => {
            this.#lazyLoadItems(isExpanded);
        });
    }
    getImagePathCallbackChanged() {
        this.#computeImage("start");
        this.#computeImage("end");
    }
    lastItemChanged(isLastItem) {
        if (isLastItem && this.showLines) {
            // Use RAF to set the observer after the render method has completed
            requestAnimationFrame(() => {
                this.#setResizeObserver();
            });
        }
        else {
            this.#disconnectObserver();
        }
    }
    partsChanged(newParts) {
        this.#setExportParts(newParts);
    }
    showLinesChanged(newShowLines) {
        if (newShowLines && this.lastItem) {
            this.#setResizeObserver();
        }
        else {
            this.#disconnectObserver();
        }
    }
    startImgSrcChanged(newImage) {
        // Necessary to avoid race condition where the image is updated, the Watch
        // is dispatched and then the model is updated
        this.model.startImgSrc = newImage;
        this.#computeImage("start");
    }
    handleToggleCheckboxesChange(newToggleCheckboxesValue) {
        if (newToggleCheckboxesValue) {
            this.el.addEventListener("checkboxChange", this.#handleCheckBoxChangeInItems);
        }
        else {
            this.el.removeEventListener("checkboxChange", this.#handleCheckBoxChangeInItems);
        }
    }
    /**
     * Fired when the checkbox value of the control is changed.
     */
    checkboxChange;
    /**
     * Fired when the checkbox value of the control is changed. This event only
     * applies when the control has `toggleCheckboxes = true`
     */
    checkboxToggleChange;
    /**
     * Fired when the item is being dragged.
     */
    itemDragStart;
    /**
     * Fired when the item is no longer being dragged.
     */
    itemDragEnd;
    /**
     * Fired when the lazy control is expanded an its content must be loaded.
     */
    loadLazyContent;
    /**
     * Fired when the item is asking to modify its caption.
     */
    modifyCaption;
    /**
     * Fired when the user interacts with the control in a way that its reference
     * must be opened.
     */
    openReference;
    /**
     * Fired when the selected state is updated by user interaction on the
     * control.
     */
    selectedItemChange;
    /**
     * Focus the next item in the tree. If the control is expanded, it focuses
     * the first subitem in its tree.
     */
    async focusNextItem(ctrlKeyPressed) {
        // Focus the first subitem if expanded
        if (!this.leaf && this.expanded) {
            const subItem = this.el.querySelector(FIRST_ENABLED_SUB_ITEM);
            // The tree item could be empty or downloading subitem, so it is uncertain
            // if the query won't fail
            if (subItem) {
                subItem.setFocus(ctrlKeyPressed);
                return;
            }
        }
        // Otherwise, focus the next sibling
        this.focusNextSibling(ctrlKeyPressed);
    }
    /**
     * Focus the next sibling item in the tree.
     */
    async focusNextSibling(ctrlKeyPressed) {
        const nextSiblingItem = this.el
            .nextElementSibling;
        // Focus the next sibling, if exists
        if (nextSiblingItem) {
            // If the next sibling is disabled, ask for its next sibling
            if (nextSiblingItem.disabled) {
                nextSiblingItem.focusNextItem(ctrlKeyPressed);
            }
            else {
                nextSiblingItem.setFocus(ctrlKeyPressed);
            }
            return;
        }
        // The item is the last one of the tree at the first level
        if (this.level === INITIAL_LEVEL) {
            return;
        }
        // Otherwise, ask the parent to focus the next sibling
        const parentItem = this.el.parentElement;
        parentItem.focusNextSibling(ctrlKeyPressed);
    }
    /**
     * Focus the previous item in the tree. If the previous item is expanded, it focuses
     * the last subitem in its tree.
     */
    async focusPreviousItem(ctrlKeyPressed) {
        const previousSiblingItem = this.el
            .previousElementSibling;
        // Focus last item of the previous sibling
        if (previousSiblingItem) {
            previousSiblingItem.focusLastItem(ctrlKeyPressed);
            return;
        }
        // The item is the first one of the tree at the first level
        if (this.level === INITIAL_LEVEL) {
            return;
        }
        // Otherwise, set focus in the parent element
        const parentItem = this.el.parentElement;
        // Check if the parent is not disabled
        if (parentItem.disabled) {
            parentItem.focusPreviousItem(ctrlKeyPressed);
            return;
        }
        parentItem.setFocus(ctrlKeyPressed);
    }
    /**
     * Focus the last item in its subtree. If the control is not expanded, it
     * focus the control.
     */
    async focusLastItem(ctrlKeyPressed) {
        // Focus the last subitem if expanded and not lazy loading
        if (!this.leaf && this.expanded) {
            const lastSubItem = this.el.querySelector(LAST_SUB_ITEM);
            // The tree item could be empty or downloading subitem, so it is uncertain
            // if the query won't fail
            if (lastSubItem) {
                lastSubItem.focusLastItem(ctrlKeyPressed);
                return;
            }
        }
        // If the last item is disabled, try to focus the previous sibling
        if (this.disabled) {
            this.focusPreviousItem(ctrlKeyPressed);
            return;
        }
        // Otherwise, it focuses the control
        this.setFocus(ctrlKeyPressed);
    }
    /**
     * Set focus in the control.
     */
    async setFocus(ctrlKeyPressed) {
        this.#headerRef.focus();
        // Normal navigation auto selects the item.
        if (!ctrlKeyPressed) {
            this.#setSelected();
        }
    }
    /**
     * Update `checked` and `indeterminate` properties.
     */
    async updateChecked(newChecked, newIndeterminate) {
        this.checked = newChecked;
        this.indeterminate = newIndeterminate;
        // Emit the event to sync with the UI model, even if the item does not
        // have toggleCheckboxes property
        this.checkboxToggleChange.emit({
            id: this.el.id,
            checked: newChecked,
            indeterminate: newIndeterminate
        });
    }
    #computeImage = (direction) => {
        if ((direction === "start" && !this.startImgSrc) ||
            (direction === "end" && !this.endImgSrc)) {
            return;
        }
        const getImagePathCallback = this.getImagePathCallback ?? GET_IMAGE_PATH_CALLBACK_REGISTRY;
        const img = getImagePathCallback(this.model, direction);
        const imageIsString = typeof img === "string";
        const parsedImg = imageIsString
            ? { base: img }
            : img?.default;
        if (direction === "start") {
            if (!img) {
                this.#startImage = null;
                this.#startImageExpanded = null;
                return;
            }
            // Add url("") wrapper for the image path as it is going to be used in a
            // background or mask
            if (imageIsString && this.startImgType !== "img") {
                parsedImg.base = `url("${parsedImg.base}")`;
            }
            // If the image is not a string, then the expanded member could be defined
            else {
                this.#startImageExpanded = this.startImgSrc
                    ? updateDirectionInImageCustomVar(img.expanded, "start", this.startImgType)
                    : undefined;
            }
            this.#startImage = this.startImgSrc
                ? updateDirectionInImageCustomVar(parsedImg, "start", this.startImgType)
                : undefined;
        }
        // End image
        else {
            if (!img) {
                this.#endImage = null;
                this.#endImageExpanded = null;
                return;
            }
            // Add url("") wrapper for the image path as it is going to be used in a
            // background or mask
            if (imageIsString && this.endImgType !== "img") {
                parsedImg.base = `url("${parsedImg.base}")`;
            }
            // If the image is not a string, then the expanded member could be defined
            else {
                this.#endImageExpanded = this.endImgSrc
                    ? updateDirectionInImageCustomVar(img.expanded, "end", this.endImgType)
                    : undefined;
            }
            this.#endImage = this.endImgSrc
                ? updateDirectionInImageCustomVar(parsedImg, "end", this.endImgType)
                : undefined;
        }
    };
    #getImageExpandedOrDefault = (base, expanded) => (this.expanded ? expanded ?? base : base);
    #getDirectTreeItems = () => Array.from(this.el.querySelectorAll(DIRECT_TREE_ITEM_CHILDREN));
    #setResizeObserver = () => {
        this.#watcher = new ResizeObserver(() => {
            const distanceToCheckbox = this.el.getBoundingClientRect().height -
                this.#headerRef.getBoundingClientRect().height / 2;
            this.el.style.setProperty(DISTANCE_TO_CHECKBOX_CUSTOM_VAR, distanceToCheckbox + "px");
        });
        this.#watcher.observe(this.el);
        this.#watcher.observe(this.#headerRef);
    };
    #disconnectObserver = () => {
        if (!this.#watcher) {
            return;
        }
        this.#watcher.disconnect();
        this.#watcher = null;
    };
    #checkIfShouldRemoveEditMode = (event) => {
        event.stopPropagation();
        if (event.code !== ENTER_KEY && event.code !== ESCAPE_KEY) {
            return;
        }
        event.preventDefault();
        const commitEdition = event.code === ENTER_KEY;
        this.#removeEditMode(true, commitEdition)();
    };
    #removeEditModeOnClick = (event) => {
        // The click is executed outside the input and the pointer type is defined,
        // meaning that the button click was not triggered by the Enter or Space keys
        if (!event.composedPath().includes(this.#inputRef) && event.pointerType) {
            this.#removeEditMode(false)();
        }
    };
    #removeEditMode = (shouldFocusHeader, commitEdition = false) => () => {
        // When pressing the enter key in the input, the removeEditMode event is
        // triggered twice (due to the headerRef.focus() triggering the onBlur
        // event in the input), so we need to check if the edit mode was disabled
        if (!this.editing) {
            return;
        }
        this.editing = false;
        document.removeEventListener("click", this.#removeEditModeOnClick, {
            capture: true
        });
        const newCaption = this.#inputRef.value;
        if (commitEdition && newCaption.trim() !== "") {
            this.modifyCaption.emit({
                id: this.el.id,
                caption: newCaption
            });
        }
        if (shouldFocusHeader) {
            this.#headerRef.focus();
        }
    };
    #toggleExpand = (event) => {
        event.stopPropagation();
        if (!this.leaf) {
            this.expanded = !this.expanded;
        }
        this.selected = true;
        this.selectedItemChange.emit(this.#getSelectedInfo(mouseEventModifierKey(event), true));
    };
    #lazyLoadItems = (expanded) => {
        if (!this.lazyLoad || !expanded) {
            return;
        }
        // Load items
        this.lazyLoad = false;
        this.downloading = true;
        this.loadLazyContent.emit(this.el.id);
    };
    #toggleSelected = () => {
        const selected = !this.selected;
        this.selected = selected;
        this.selectedItemChange.emit(this.#getSelectedInfo(true, selected));
    };
    #setSelected = () => {
        this.selected = true;
        this.selectedItemChange.emit(this.#getSelectedInfo(false, true));
    };
    #toggleOrSelect = (event) => {
        // Ctrl key
        if (mouseEventModifierKey(event)) {
            this.#toggleSelected();
            return;
        }
        // Double click was triggered, don't update the selection or expand
        if (event.detail >= 2) {
            return;
        }
        // Expand on click interaction
        if (this.expandOnClick) {
            this.#toggleExpand(event);
        }
        // Click only selects the item
        else {
            this.#setSelected();
        }
    };
    #getSelectedInfo = (ctrlKeyPressed, selected) => ({
        ctrlKeyPressed: ctrlKeyPressed,
        expanded: this.expanded,
        id: this.el.id,
        metadata: this.metadata,
        parentId: this.el.parentElement?.id, // TODO: Improve this
        selected: selected
    });
    #handleActionDblClick = (event) => {
        event.stopPropagation();
        if (mouseEventModifierKey(event)) {
            this.#toggleSelected();
            return;
        }
        this.#emitOpenReference();
        // The Control key is not pressed, so the control can be expanded if double
        // click expands the item
        if (!this.leaf && !this.expandOnClick) {
            this.#toggleExpand(event);
        }
    };
    /**
     * Event triggered by the following actions on the main button:
     *   - Click
     *   - Enter keydown
     *   - Space keydown and keyup
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #handleActionClick = (event) => {
        event.stopPropagation();
        // Don't perform actions when editing
        if (this.editing) {
            return;
        }
        event.preventDefault();
        // Click event
        if (event.pointerType) {
            this.#toggleOrSelect(event);
            return;
        }
        // The action was provoked by the keyboard, emit openReference event
        this.#emitOpenReference();
        // Enter or space
        this.#toggleExpand(event);
    };
    /**
     * Event triggered by key events on the main button.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #handleActionKeyDown = (event) => {
        // Only toggle if the Enter key was pressed with the Ctrl key
        if (mouseEventModifierKey(event) && event.code === ENTER_KEY) {
            event.stopPropagation();
            this.#toggleSelected();
        }
    };
    #emitOpenReference = () => {
        this.openReference.emit({
            id: this.el.id,
            leaf: this.leaf,
            metadata: this.metadata
        });
    };
    #handleCheckedChange = (event) => {
        event.stopPropagation();
        const chCheckboxRef = event.target;
        const checked = chCheckboxRef.checkedValue === chCheckboxRef.value;
        this.checked = checked;
        this.indeterminate = false; // Changing the checked value makes it no longer indeterminate
        this.checkboxChange.emit({
            id: this.el.id,
            checked: this.checked,
            indeterminate: false
        });
    };
    #handleCheckBoxChangeInItems = (event) => {
        // No need to update the checkbox value based on the children checkbox
        if (this.el === event.target) {
            return;
        }
        const updatedCheck = event.detail.checked;
        const treeItems = this.#getDirectTreeItems();
        // Check if all the items have the same value as the updated item
        const allItemsHaveTheSameCheckedValue = treeItems.every(treeItem => treeItem.checked === updatedCheck);
        const eventMustBeEmitted = this.checked !== updatedCheck ||
            this.indeterminate !== !allItemsHaveTheSameCheckedValue;
        this.#ignoreCheckboxChange = this.checked !== updatedCheck;
        this.checked = updatedCheck;
        this.indeterminate = !allItemsHaveTheSameCheckedValue;
        // Sync with the UI Model
        if (eventMustBeEmitted) {
            this.checkboxToggleChange.emit({
                id: this.el.id,
                checked: updatedCheck,
                indeterminate: !allItemsHaveTheSameCheckedValue
            });
        }
    };
    #renderImg = (cssClass, src, imageType) => imageType === "img" && (h("img", { "aria-hidden": "true", class: `img ${cssClass}`, part: cssClass, alt: "", src: src, loading: "lazy" }));
    #handleDragStart = (event) => {
        // Disallow drag when editing the caption
        if (this.editing) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        removeDragImage(event);
        event.dataTransfer.effectAllowed = "move";
        this.dragState = "start";
        this.itemDragStart.emit({
            elem: this.el,
            dragEvent: event
        });
    };
    #handleDragEnd = () => {
        // event.preventDefault();
        // this.el.style.cursor = null;
        this.dragState = "none";
        this.itemDragEnd.emit();
    };
    #setExportParts = (exportparts) => {
        if (exportparts) {
            this.el.setAttribute("exportparts", 
            // Replace sequential empty characters with a comma
            // TODO: Use replaceAll?
            `${TREE_VIEW_ITEM_EXPORT_PARTS},${exportparts.replace(/\s+/g, ",")}`);
        }
        else {
            this.el.setAttribute("exportparts", TREE_VIEW_ITEM_EXPORT_PARTS);
        }
    };
    connectedCallback() {
        // Initialize default getImagePathCallback
        GET_IMAGE_PATH_CALLBACK_REGISTRY ??= getControlRegisterProperty("getImagePathCallback", "ch-tree-view-render");
        if (this.toggleCheckboxes) {
            this.el.addEventListener("checkboxChange", this.#handleCheckBoxChangeInItems);
        }
        this.#computeImage("start");
        this.#computeImage("end");
        // Static attributes that we including in the Host functional component to
        // eliminate additional overhead
        this.el.setAttribute("role", "treeitem");
        this.el.setAttribute("aria-level", `${this.level + 1}`);
        this.el.style.setProperty("--level", `${this.level}`);
        this.#setExportParts(this.parts);
    }
    componentWillLoad() {
        // Check if must lazy load
        this.#lazyLoadItems(this.expanded);
    }
    componentDidLoad() {
        if (this.lastItem && this.showLines) {
            this.#setResizeObserver();
        }
    }
    disconnectedCallback() {
        // If it was disconnected on edit mode, remove the body event handler
        if (this.editing) {
            this.#removeEditMode(false);
        }
        this.#disconnectObserver();
        this.el.removeEventListener("checkboxChange", this.#handleCheckBoxChangeInItems);
    }
    render() {
        const evenLevel = this.level % 2 === 0;
        const hasContent = !this.leaf && !this.lazyLoad;
        const canShowLines = this.level !== INITIAL_LEVEL;
        const showAllLines = this.showLines === "all" && canShowLines;
        const showLastLine = this.showLines === "last" && canShowLines && this.lastItem;
        const levelPart = getTreeItemLevelPart(evenLevel);
        const expandedPart = getTreeItemExpandedPart(this.expanded);
        const pseudoStartImage = isPseudoElementImg(this.startImgSrc, this.startImgType);
        const pseudoEndImage = isPseudoElementImg(this.endImgSrc, this.endImgType);
        const hasParts = !!this.parts;
        return (h(Host, { key: '340bbee2edc60b2d3978f049cd8cef83223b4724', "aria-selected": this.selected ? "true" : null, class: this.leaf ? DENY_DROP_CLASS : null, part: tokenMap({
                [TREE_VIEW_PARTS_DICTIONARY.ITEM]: true,
                [TREE_VIEW_PARTS_DICTIONARY.DRAG_ENTER]: this.dragState === "enter",
                [this.parts]: hasParts
            }) }, h("button", { key: '48084c1d7fa56ff6b92903ea876a911b1931f17b', "aria-controls": hasContent ? EXPANDABLE_ID : null, "aria-expanded": hasContent ? this.expanded.toString() : null, class: {
                header: true,
                "header--selected": this.selected,
                "header--disabled": this.disabled,
                "expandable-button-decorative": !this.leaf && this.expandableButton === "decorative",
                "expandable-button-decorative--collapsed": !this.leaf &&
                    this.expandableButton === "decorative" &&
                    !this.expanded
            }, part: tokenMap({
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.HEADER]: true,
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.DRAG_ENTER]: this.dragState === "enter",
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.SELECTED]: this.selected,
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.NOT_SELECTED]: !this.selected,
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.EXPAND_BUTTON]: canShowLines && !this.leaf && this.expandableButton !== "no",
                [this.editing
                    ? TREE_VIEW_ITEM_PARTS_DICTIONARY.EDITING
                    : TREE_VIEW_ITEM_PARTS_DICTIONARY.NOT_EDITING]: true,
                [levelPart]: canShowLines,
                // TODO: Add unit test
                // Undocumented. Only used for implementation purposes
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.LEVEL_0_LEAF]: !canShowLines && this.leaf && this.expandableButton !== "no",
                [this.parts]: hasParts
            }), style: pseudoStartImage && (this.#startImage || this.#startImageExpanded)
                ? this.#getImageExpandedOrDefault(this.#startImage, this.#startImageExpanded).styles
                : undefined, type: "button", disabled: this.disabled, onClick: this.#handleActionClick, onKeyDown: !this.editing ? this.#handleActionKeyDown : null,
            // Drag and drop
            draggable: !this.dragDisabled, onDragStart: this.#handleDragStart, onDragEnd: !this.dragDisabled && this.dragState === "start"
                ? this.#handleDragEnd
                : null, ref: el => (this.#headerRef = el) }, !this.leaf && this.expandableButton === "action" && (h("button", { key: "expandable-button", type: "button", class: {
                "expandable-button": true,
                "expandable-button--expanded": this.expanded,
                "expandable-button--collapsed": !this.expanded
            }, part: tokenMap({
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.EXPANDABLE_BUTTON]: true,
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.DISABLED]: this.disabled,
                [expandedPart]: true,
                [this.parts]: hasParts
            }), disabled: this.disabled, onClick: this.#toggleExpand })), this.checkbox && (h("ch-checkbox", { key: "checkbox", accessibleName: this.caption, class: "checkbox", exportparts: TREE_VIEW_ITEM_CHECKBOX_EXPORT_PARTS, part: hasParts
                ? `${TREE_VIEW_ITEM_PARTS_DICTIONARY.CHECKBOX} ${this.parts}`
                : TREE_VIEW_ITEM_PARTS_DICTIONARY.CHECKBOX, checkedValue: "true", disabled: this.disabled, indeterminate: this.indeterminate, unCheckedValue: "false", value: `${this.checked}`, onInput: this.#handleCheckedChange })), this.customRender ? (h("slot", { name: "custom-content" })) : ([
            h("div", { class: {
                    action: true,
                    "action--end-img": !!this.endImgSrc,
                    [`start-img-type--${this.startImgType} pseudo-img--start`]: pseudoStartImage,
                    [`end-img-type--${this.endImgType} pseudo-img--end`]: pseudoEndImage
                }, part: tokenMap({
                    [TREE_VIEW_ITEM_PARTS_DICTIONARY.ACTION]: true,
                    [this.editing
                        ? TREE_VIEW_ITEM_PARTS_DICTIONARY.EDITING
                        : TREE_VIEW_ITEM_PARTS_DICTIONARY.NOT_EDITING]: true,
                    [expandedPart]: !this.leaf,
                    [this.parts]: hasParts
                }), style: pseudoEndImage && (this.#endImage || this.#endImageExpanded)
                    ? this.#getImageExpandedOrDefault(this.#endImage, this.#endImageExpanded).styles
                    : undefined, onDblClick: !this.editing ? this.#handleActionDblClick : null }, this.startImgSrc &&
                this.#startImage &&
                this.#renderImg(hasParts
                    ? `${START_IMAGE_PARTS} ${this.parts}`
                    : START_IMAGE_PARTS, this.#startImage.styles["--ch-start-img--base"], this.startImgType), this.editable && this.editing ? (h("input", { key: "edit-caption", class: "edit-caption", part: hasParts
                    ? `${TREE_VIEW_ITEM_PARTS_DICTIONARY.EDIT_CAPTION} ${this.parts}`
                    : TREE_VIEW_ITEM_PARTS_DICTIONARY.EDIT_CAPTION, disabled: this.disabled, type: "text", value: this.caption, onBlur: this.#removeEditMode(false), onKeyDown: this.#checkIfShouldRemoveEditMode, ref: el => (this.#inputRef = el) })) : (this.caption), this.endImgSrc &&
                this.#endImage &&
                this.#renderImg(hasParts
                    ? `${END_IMAGE_PARTS} ${this.parts}`
                    : END_IMAGE_PARTS, this.#endImage.styles["--ch-end-img--base"], this.endImgType)),
            this.showDownloadingSpinner && !this.leaf && this.downloading && (h("div", { class: "downloading", part: hasParts
                    ? `${TREE_VIEW_ITEM_PARTS_DICTIONARY.DOWNLOADING} ${this.parts}`
                    : TREE_VIEW_ITEM_PARTS_DICTIONARY.DOWNLOADING }))
        ]), (showAllLines || showLastLine) && (h("div", { key: "line", class: {
                line: true,
                "last-all-line": showAllLines && this.lastItem,
                "last-line": showLastLine
            }, part: tokenMap({
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.LINE]: true,
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.LAST_LINE]: this.lastItem,
                [this.parts]: hasParts
            }) }))), hasContent && (h("div", { key: '5cee089a1ced332430aeeae48399109a566eff16', role: "group", "aria-busy": (!!this.downloading).toString(), "aria-live": this.downloading ? "polite" : null, id: EXPANDABLE_ID, class: {
                expandable: true,
                "expandable--collapsed": !this.expanded,
                "expandable--lazy-loaded": !this.downloading,
                "expandable--even": canShowLines && evenLevel,
                "expandable--odd": canShowLines && !evenLevel
            }, part: tokenMap({
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.GROUP]: true,
                [TREE_VIEW_ITEM_PARTS_DICTIONARY.LAZY_LOADED]: !this.downloading,
                [expandedPart]: true,
                [levelPart]: canShowLines,
                [this.parts]: hasParts
            }) }, h("slot", { key: '6a530f3e66150004aafac66f292393c476be263b' })))));
    }
    static get is() { return "ch-tree-view-item"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["tree-view-item.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tree-view-item.css"]
        };
    }
    static get properties() {
        return {
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attributes specifies the caption of the control"
                },
                "attribute": "caption",
                "reflect": false
            },
            "checkbox": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want display a checkbox in the control."
                },
                "attribute": "checkbox",
                "reflect": false,
                "defaultValue": "false"
            },
            "checked": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want the checkbox to be checked by default.\nOnly works if `checkbox = true`"
                },
                "attribute": "checked",
                "reflect": true,
                "defaultValue": "false"
            },
            "customRender": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want to set a custom render for the control, by\npassing a slot."
                },
                "attribute": "custom-render",
                "reflect": false,
                "defaultValue": "false"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "dragDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drag operation is disabled in the\ncontrol. If `true`, the control can't be dragged."
                },
                "attribute": "drag-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "dropDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drop operation is disabled in the\ncontrol. If `true`, the control won't accept any drops."
                },
                "attribute": "drop-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "dragState": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DragState",
                    "resolved": "\"enter\" | \"none\" | \"start\"",
                    "references": {
                        "DragState": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/tree-view/internal/tree-view-item/tree-view-item.tsx",
                            "id": "src/components/tree-view/internal/tree-view-item/tree-view-item.tsx::DragState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you define the current state of the item when it's\nbeing dragged."
                },
                "attribute": "drag-state",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "downloading": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify when items are being lazy loaded in the\ncontrol."
                },
                "attribute": "downloading",
                "reflect": false,
                "defaultValue": "false"
            },
            "editable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the edit operation is enabled in the\ncontrol. If `true`, the control can edit its caption in place."
                },
                "attribute": "editable",
                "reflect": false
            },
            "editing": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute when the item is in edit mode"
                },
                "attribute": "editing",
                "reflect": false,
                "defaultValue": "false"
            },
            "endImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the src of the end image."
                },
                "attribute": "end-img-src",
                "reflect": false
            },
            "endImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ImageRender",
                    "resolved": "\"background\" | \"img\" | \"mask\"",
                    "references": {
                        "ImageRender": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the end image will be rendered."
                },
                "attribute": "end-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            },
            "expandableButton": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"action\" | \"decorative\" | \"no\"",
                    "resolved": "\"action\" | \"decorative\" | \"no\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies what kind of expandable button is displayed.\nOnly works if `leaf === false`.\n - `\"expandableButton\"`: Expandable button that allows to expand/collapse\n    the items of the control.\n - `\"decorative\"`: Only a decorative icon is rendered to display the state\n    of the item."
                },
                "attribute": "expandable-button",
                "reflect": false,
                "defaultValue": "\"decorative\""
            },
            "expandOnClick": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to expand the control on click interaction. If `false`, with mouse\ninteraction the control will only be expanded on double click."
                },
                "attribute": "expand-on-click",
                "reflect": false,
                "defaultValue": "true"
            },
            "expanded": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If the item has a sub-tree, this attribute determines if the subtree is\ndisplayed."
                },
                "attribute": "expanded",
                "reflect": false,
                "defaultValue": "false"
            },
            "getImagePathCallback": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewImagePathCallback",
                    "resolved": "(item: TreeViewItemModel, iconDirection: \"start\" | \"end\") => string | TreeViewItemImageMultiState",
                    "references": {
                        "TreeViewImagePathCallback": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/components/tree-view/types.ts::TreeViewImagePathCallback"
                        }
                    }
                },
                "required": true,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property specifies a callback that is executed when the path for an\nitem image needs to be resolved."
                }
            },
            "lastItem": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute specifies if the control is the last items in its subtree"
                },
                "attribute": "last-item",
                "reflect": false,
                "defaultValue": "false"
            },
            "lazyLoad": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine if the items are lazy loaded when opening the first time the\ncontrol."
                },
                "attribute": "lazy-load",
                "reflect": false,
                "defaultValue": "false"
            },
            "leaf": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The presence of this attribute determine whether the item contains a\nsubtree. `true` if the item does not have a subtree."
                },
                "attribute": "leaf",
                "reflect": false,
                "defaultValue": "false"
            },
            "level": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Level in the tree at which the item is placed."
                },
                "attribute": "level",
                "reflect": false,
                "defaultValue": "INITIAL_LEVEL"
            },
            "indeterminate": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the checkbox's value is indeterminate."
                },
                "attribute": "indeterminate",
                "reflect": false,
                "defaultValue": "false"
            },
            "metadata": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute represents additional info for the control that is included\nwhen dragging the item."
                },
                "attribute": "metadata",
                "reflect": false
            },
            "model": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewItemModel",
                    "resolved": "{ id: string; caption: string; checkbox?: boolean; checked?: boolean; disabled?: boolean; downloading?: boolean; dragDisabled?: boolean; dropDisabled?: boolean; editable?: boolean; expanded?: boolean; endImgSrc?: string; endImgType?: ImageRender; lastItemId?: string; lazy?: boolean; leaf?: boolean; indeterminate?: boolean; items?: TreeViewModel; metadata?: string; order?: number; parts?: string; render?: boolean; selected?: boolean; startImgSrc?: string; startImgType?: ImageRender; toggleCheckboxes?: boolean; }",
                    "references": {
                        "TreeViewItemModel": {
                            "location": "import",
                            "path": "../../types",
                            "id": "src/components/tree-view/types.ts::TreeViewItemModel"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the model of the item."
                }
            },
            "parts": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": true,
                "docs": {
                    "tags": [],
                    "text": "Specifies a set of parts to use in every DOM element of the control."
                },
                "attribute": "parts",
                "reflect": false
            },
            "selected": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the item is selected"
                },
                "attribute": "selected",
                "reflect": true,
                "defaultValue": "false"
            },
            "showDownloadingSpinner": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to show the downloading spinner when lazy loading the sub items of\nthe control."
                },
                "attribute": "show-downloading-spinner",
                "reflect": false,
                "defaultValue": "true"
            },
            "showLines": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewLines",
                    "resolved": "\"all\" | \"last\" | \"none\"",
                    "references": {
                        "TreeViewLines": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/internal/tree-view/types.ts::TreeViewLines"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display the relation between tree items and tree lists using\nlines."
                },
                "attribute": "show-lines",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "startImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies the src of the start image."
                },
                "attribute": "start-img-src",
                "reflect": false
            },
            "startImgType": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "ImageRender",
                    "resolved": "\"background\" | \"img\" | \"mask\"",
                    "references": {
                        "ImageRender": {
                            "location": "import",
                            "path": "../../../../common/types",
                            "id": "src/common/types.ts::ImageRender"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies how the start image will be rendered."
                },
                "attribute": "start-img-type",
                "reflect": false,
                "defaultValue": "\"background\""
            },
            "toggleCheckboxes": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want all the children item's checkboxes to be\nchecked when the parent item checkbox is checked, or to be unchecked when\nthe parent item checkbox is unchecked."
                },
                "attribute": "toggle-checkboxes",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "checkboxChange",
                "name": "checkboxChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the checkbox value of the control is changed."
                },
                "complexType": {
                    "original": "TreeViewItemCheckedInfo",
                    "resolved": "{ id: string; checked: boolean; indeterminate: boolean; }",
                    "references": {
                        "TreeViewItemCheckedInfo": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/internal/tree-view/types.ts::TreeViewItemCheckedInfo"
                        }
                    }
                }
            }, {
                "method": "checkboxToggleChange",
                "name": "checkboxToggleChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the checkbox value of the control is changed. This event only\napplies when the control has `toggleCheckboxes = true`"
                },
                "complexType": {
                    "original": "TreeViewItemCheckedInfo",
                    "resolved": "{ id: string; checked: boolean; indeterminate: boolean; }",
                    "references": {
                        "TreeViewItemCheckedInfo": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/internal/tree-view/types.ts::TreeViewItemCheckedInfo"
                        }
                    }
                }
            }, {
                "method": "itemDragStart",
                "name": "itemDragStart",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is being dragged."
                },
                "complexType": {
                    "original": "TreeViewItemDragStartInfo",
                    "resolved": "{ elem: HTMLChTreeViewItemElement; dragEvent: DragEvent; }",
                    "references": {
                        "TreeViewItemDragStartInfo": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/internal/tree-view/types.ts::TreeViewItemDragStartInfo"
                        }
                    }
                }
            }, {
                "method": "itemDragEnd",
                "name": "itemDragEnd",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is no longer being dragged."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "loadLazyContent",
                "name": "loadLazyContent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the lazy control is expanded an its content must be loaded."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "modifyCaption",
                "name": "modifyCaption",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is asking to modify its caption."
                },
                "complexType": {
                    "original": "TreeViewItemNewCaption",
                    "resolved": "{ id: string; caption: string; }",
                    "references": {
                        "TreeViewItemNewCaption": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/internal/tree-view/types.ts::TreeViewItemNewCaption"
                        }
                    }
                }
            }, {
                "method": "openReference",
                "name": "openReference",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the user interacts with the control in a way that its reference\nmust be opened."
                },
                "complexType": {
                    "original": "TreeViewItemOpenReferenceInfo",
                    "resolved": "{ id: string; leaf: boolean; metadata: string; }",
                    "references": {
                        "TreeViewItemOpenReferenceInfo": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/internal/tree-view/types.ts::TreeViewItemOpenReferenceInfo"
                        }
                    }
                }
            }, {
                "method": "selectedItemChange",
                "name": "selectedItemChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected state is updated by user interaction on the\ncontrol."
                },
                "complexType": {
                    "original": "TreeViewItemSelected",
                    "resolved": "{ ctrlKeyPressed: boolean; expanded: boolean; id: string; metadata: string; parentId: string; selected: boolean; }",
                    "references": {
                        "TreeViewItemSelected": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/internal/tree-view/types.ts::TreeViewItemSelected"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "focusNextItem": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTreeViewItemElement": {
                            "location": "global",
                            "id": "global::HTMLChTreeViewItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the next item in the tree. If the control is expanded, it focuses\nthe first subitem in its tree.",
                    "tags": []
                }
            },
            "focusNextSibling": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTreeViewItemElement": {
                            "location": "global",
                            "id": "global::HTMLChTreeViewItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the next sibling item in the tree.",
                    "tags": []
                }
            },
            "focusPreviousItem": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTreeViewItemElement": {
                            "location": "global",
                            "id": "global::HTMLChTreeViewItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the previous item in the tree. If the previous item is expanded, it focuses\nthe last subitem in its tree.",
                    "tags": []
                }
            },
            "focusLastItem": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTreeViewItemElement": {
                            "location": "global",
                            "id": "global::HTMLChTreeViewItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the last item in its subtree. If the control is not expanded, it\nfocus the control.",
                    "tags": []
                }
            },
            "setFocus": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Set focus in the control.",
                    "tags": []
                }
            },
            "updateChecked": {
                "complexType": {
                    "signature": "(newChecked: boolean, newIndeterminate: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "newChecked",
                            "type": "boolean",
                            "docs": ""
                        }, {
                            "name": "newIndeterminate",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update `checked` and `indeterminate` properties.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "checked",
                "methodName": "updateChildrenCheckedValue"
            }, {
                "propName": "editing",
                "methodName": "editingChanged"
            }, {
                "propName": "endImgSrc",
                "methodName": "endImgSrcChanged"
            }, {
                "propName": "expanded",
                "methodName": "expandedChanged"
            }, {
                "propName": "getImagePathCallback",
                "methodName": "getImagePathCallbackChanged"
            }, {
                "propName": "lastItem",
                "methodName": "lastItemChanged"
            }, {
                "propName": "parts",
                "methodName": "partsChanged"
            }, {
                "propName": "showLines",
                "methodName": "showLinesChanged"
            }, {
                "propName": "startImgSrc",
                "methodName": "startImgSrcChanged"
            }, {
                "propName": "toggleCheckboxes",
                "methodName": "handleToggleCheckboxesChange"
            }];
    }
}
